/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import {useQuery} from 'react-query'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {resolveChartColor, withPremiumTheme} from './chart-theme'
import {Graphic2Type} from '../core/_models'
import {getGraphic2} from '../core/_requests'
import {useAuth} from '../../../../app/modules/auth'
import {useMenuInnerContext} from "../../../../_metronic/layout/components/header/header-menus/MenuInnerContext"

type Props = {
  className: string
  chartColor: string
  chartHeight: string
}

const Graphic2: React.FC<Props> = ({ className, chartColor, chartHeight }) => {
  const {currentUser} = useAuth()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const [refreshGraphic, setRefreshGraphic] = useState(false)

  const menuInnerContext = useMenuInnerContext()

  const query = useQuery(
    'interventions-username',
    () => getGraphic2(currentUser!.last_company_id, menuInnerContext.dashboardStartDate, menuInnerContext.dashboardEndDate), {
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })

  const refreshChart = () => {
    if (!chartRef.current || query.isFetching) {
      return
    }

    const chart1 = new ApexCharts(chartRef.current, chart1Options(chartColor, chartHeight, query.data))
    if (chart1) {
      chart1.render()
    }

    setRefreshGraphic(false)
    return chart1
  }

  useEffect(() => {
    query.refetch().then(() => {
      setRefreshGraphic(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, menuInnerContext.dashboardStartDate, menuInnerContext.dashboardEndDate])

  useEffect(() => {
    menuInnerContext.addFetchingGraphic('graphic2', query.isFetching)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isFetching])

  useEffect(() => {
    const chart1 = refreshChart()

    return () => {
      if (chart1) {
        chart1.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, query.data, refreshGraphic])

  return (
    <div className={className}>
      {(query.data && query.data.length > 0) ? (
        <div ref={chartRef} ></div>
      ) : (
        <div className='d-flex flex-1 justify-content-center align-items-center fs-7' style={{ color: '#9ca3af', minHeight: 180 }}>
          {query.isFetching ? (
            <div className="spinner-border text-secondary spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : 'Sem dados'}
        </div>
      )}
    </div>
  )
}

const chart1Options = (chartColor: string, chartHeight: string, data: Graphic2Type[] | undefined): ApexOptions => {
  const baseColor = resolveChartColor(chartColor);

  return withPremiumTheme({
    series: [{
      name: 'Total de intervenções realizadas',
      data: data ? data.map((box) => box.total_opening) : []
    }],
    chart: { type: 'bar', height: chartHeight },
    plotOptions: { bar: { horizontal: false, columnWidth: '60', borderRadius: 4 } },
    legend: { show: false },
    stroke: { show: false },
    xaxis: {
      categories: data ? data.sort((a, b) => b.total_opening - a.total_opening).map((box) => box.user_name.split(' ').map((name) => {
        return name.substring(0,1).toUpperCase() + name.slice(1).toLowerCase()
      }).join(' ')) : [],
      labels: {
        formatter: function (val) {
          const fullName = String(val).trim()
          const numParts = fullName.split(' ').length
          const firstName = fullName.split(' ')[0]
          const lastNameLetter = numParts > 1 && fullName.split(' ')[numParts - 1][0]
          return lastNameLetter ? `${firstName} ${lastNameLetter}.` : firstName
        },
      },
    },
    yaxis: { labels: { formatter: (val) => val.toString() } },
    fill: { opacity: 0.95 },
    tooltip: {
      x: { formatter: (val) => 'Usuário: ' + val },
      y: { formatter: (val) => val.toString() },
    },
    colors: [baseColor],
  });
}

export { Graphic2 }
